import { z } from 'zod/v4';

import { endpoint } from '../http/endpoint';
import { PcbDesignIdRuntype, PcbIdRuntype } from '../idTypes';
import {
    CalculatedPricesRuntype,
    CapabilitiesDeploymentDescriptorRuntype,
    CapabilitiesDeploymentRuntype,
    CapabilitiesRuntype,
    CapabilityCheckResultRuntype,
    DecisionTableRuntype,
    DeployedCapabilitiesRuntype,
    DeployedPricingRuntype,
    ExtendedPriceRequestRuntype,
    PcbSupplierReferenceRuntype,
    PriceRequestRuntype,
    PricingDeploymentDescriptorRuntype,
    PricingDescriptorRuntype,
    PricingExportRuntype,
    PricingRuntype,
    PricingVariableRuntype,
    VersionedDecisionTableRuntype,
} from './priceBackendTypes';

/**
 * Endpoints of the Stackrate price service (scala-workspace/framework/price, served under /api/price).
 * Pricings are versioned decision tables: editing a table means deploying a new version and
 * activating it. Deletes are deliberately NOT part of this surface.
 */
export const priceEndpoints = {
    // ------------------------------------------------------------------
    // Reads
    // ------------------------------------------------------------------

    'GET /price/calculation': endpoint({
        description: 'Lists all deployed pricings of the team (unbounded).',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(DeployedPricingRuntype),
        actionPermission: () => 'CanViewPcbPricing',
    }),

    'GET /price/calculation/:nameOrID': endpoint({
        description: 'A deployed pricing with its decision tables (all versions), by name or UUID.',
        pathParams: z.object({ nameOrID: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PricingRuntype,
        actionPermission: () => 'CanViewPcbPricing',
    }),

    'GET /price/calculation/:nameOrID/export': endpoint({
        description: 'Exports a deployed pricing as a re-deployable descriptor (current table versions only).',
        pathParams: z.object({ nameOrID: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: PricingExportRuntype,
        actionPermission: () => 'CanViewPcbPricing',
    }),

    'GET /price/calculation/:nameOrID/variables': endpoint({
        description:
            'The variables referenced by a pricing’s decision tables. With include=all, the system-defined variables injected at calculation time (pcb_*/specification_*/layerstack_*/order_*/panel_*/customer_*) are returned as well.',
        pathParams: z.object({ nameOrID: z.string() }),
        queryParams: z.object({ include: z.enum(['all']).optional() }),
        requestBody: z.undefined(),
        responseBody: z.array(PricingVariableRuntype),
        actionPermission: () => 'CanViewPcbPricing',
    }),

    'GET /price/calculation/:nameOrID/tables/:table': endpoint({
        description: 'The current version of a single decision table of a pricing.',
        pathParams: z.object({ nameOrID: z.string(), table: z.string() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: DecisionTableRuntype,
        actionPermission: () => 'CanViewPcbPricing',
    }),

    'GET /price/calculation/:nameOrID/tables/:table/versions/:version': endpoint({
        description: 'A specific version of a single decision table of a pricing.',
        pathParams: z.object({ nameOrID: z.string(), table: z.string(), version: z.number() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: DecisionTableRuntype,
        actionPermission: () => 'CanViewPcbPricing',
    }),

    'GET /price/capabilities': endpoint({
        description: 'The capability tables of all suppliers of the team.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: z.array(DeployedCapabilitiesRuntype),
        actionPermission: () => 'CanViewPcbPricing',
    }),

    'GET /price/capabilities/:supplier': endpoint({
        description:
            'The capability tables of a single supplier. Resolves the supplier reference like the writes but never auto-creates: a supplier-and-stock-location ID that no pcb supplier references yet yields a 404.',
        pathParams: z.object({ supplier: PcbSupplierReferenceRuntype }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: DeployedCapabilitiesRuntype,
        actionPermission: () => 'CanViewPcbPricing',
    }),

    // ------------------------------------------------------------------
    // Calculation (non-mutating despite PUT)
    // ------------------------------------------------------------------

    'PUT /price/prices/assemblies/:assembly/versions/:version/specifications/:specification/calculate': endpoint({
        description:
            'Calculates prices for an assembly version + specification against every deployed pricing. Prices are ephemeral. Panel can be passed via the panel query parameter.',
        pathParams: z.object({ assembly: PcbDesignIdRuntype, version: PcbIdRuntype, specification: z.string() }),
        queryParams: z.object({ panel: z.string().optional() }),
        requestBody: PriceRequestRuntype,
        responseBody: CalculatedPricesRuntype,
        actionPermission: () => 'CanCalculatePcbPrice',
    }),

    'PUT /price/prices/assemblies/:assembly/versions/:version/specifications/:specification/panels/:panel/calculate':
        endpoint({
            description: 'Same calculation as the query-parameter variant, with the panel in the path.',
            pathParams: z.object({
                assembly: PcbDesignIdRuntype,
                version: PcbIdRuntype,
                specification: z.string(),
                panel: z.string(),
            }),
            queryParams: z.undefined(),
            requestBody: PriceRequestRuntype,
            responseBody: CalculatedPricesRuntype,
            actionPermission: () => 'CanCalculatePcbPrice',
        }),

    'PUT /price/prices/shares/:share/specifications/:specification/calculate': endpoint({
        description: 'Calculates prices for a shared assembly (only pricings with enableApi).',
        pathParams: z.object({ share: z.string(), specification: z.string() }),
        queryParams: z.object({ panel: z.string().optional() }),
        requestBody: ExtendedPriceRequestRuntype,
        responseBody: CalculatedPricesRuntype,
        actionPermission: () => 'CanCalculatePcbPrice',
    }),

    'PUT /price/prices/calculate': endpoint({
        description:
            'Calculates prices from caller-supplied PCB/specification/layerstack metadata instead of a stored assembly — the tweak-and-recalculate loop.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: ExtendedPriceRequestRuntype,
        responseBody: CalculatedPricesRuntype,
        actionPermission: () => 'CanCalculatePcbPrice',
    }),

    'PUT /price/capabilitycheck/bypcb': endpoint({
        description:
            'Checks a PCB (the full PCBV2 blob, as returned by GET /ems/pcb/v2/pcbs/:pcbId) against the capability tables of all suppliers.',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: z.unknown(),
        responseBody: z.array(CapabilityCheckResultRuntype),
        actionPermission: () => 'CanCalculatePcbPrice',
    }),

    // ------------------------------------------------------------------
    // Writes (create + edit — deletes are deliberately excluded)
    // ------------------------------------------------------------------

    'POST /price/calculation': endpoint({
        description:
            'Deploys a new pricing with its decision tables. Pass the luminovo supplier-and-stock-location ID as `supplier` (see PcbSupplierReferenceRuntype).',
        pathParams: z.undefined(),
        queryParams: z.undefined(),
        requestBody: PricingDeploymentDescriptorRuntype,
        responseBody: PricingRuntype,
        invalidates: ['GET /price/calculation'],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'PUT /price/calculation/:nameOrID': endpoint({
        description:
            'Changes an existing pricing (name, supplier, enableApi, currency, table order). Pass the luminovo supplier-and-stock-location ID as `supplier` (see PcbSupplierReferenceRuntype).',
        pathParams: z.object({ nameOrID: z.string() }),
        queryParams: z.undefined(),
        requestBody: PricingDescriptorRuntype,
        responseBody: DeployedPricingRuntype,
        invalidates: ['GET /price/calculation', 'GET /price/calculation/:nameOrID'],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'POST /price/calculation/:nameOrID/tables': endpoint({
        description: 'Adds a new decision table to a pricing.',
        pathParams: z.object({ nameOrID: z.string() }),
        queryParams: z.undefined(),
        requestBody: DecisionTableRuntype,
        responseBody: PricingRuntype,
        invalidates: ['GET /price/calculation', 'GET /price/calculation/:nameOrID'],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'POST /price/calculation/:nameOrID/tables/:table': endpoint({
        description:
            'Deploys a new version of a decision table. The new version still has to be activated via the versions/:version/use endpoint.',
        pathParams: z.object({ nameOrID: z.string(), table: z.string() }),
        queryParams: z.undefined(),
        requestBody: DecisionTableRuntype,
        responseBody: VersionedDecisionTableRuntype,
        invalidates: ['GET /price/calculation/:nameOrID', 'GET /price/calculation/:nameOrID/tables/:table'],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'POST /price/calculation/:nameOrID/tables/:table/versions/:version/use': endpoint({
        description: 'Activates a specific version of a decision table.',
        pathParams: z.object({ nameOrID: z.string(), table: z.string(), version: z.number() }),
        queryParams: z.undefined(),
        requestBody: z.undefined(),
        responseBody: VersionedDecisionTableRuntype,
        invalidates: [
            'GET /price/calculation/:nameOrID',
            'GET /price/calculation/:nameOrID/tables/:table',
            'GET /price/calculation/:nameOrID/tables/:table/versions/:version',
        ],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'POST /price/capabilities/:supplier': endpoint({
        description:
            'Deploys the capability tables of a supplier. The path parameter is the normalized supplier reference; a mismatching body `supplier` is ignored (the path wins).',
        pathParams: z.object({ supplier: PcbSupplierReferenceRuntype }),
        queryParams: z.undefined(),
        requestBody: CapabilitiesDeploymentRuntype,
        responseBody: DeployedCapabilitiesRuntype,
        invalidates: ['GET /price/capabilities', 'GET /price/capabilities/:supplier'],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'PUT /price/capabilities/:supplier': endpoint({
        description:
            'Changes the capability tables of a supplier (reorder). The path parameter is the normalized supplier reference; a mismatching body `supplier` is ignored (the path wins).',
        pathParams: z.object({ supplier: PcbSupplierReferenceRuntype }),
        queryParams: z.undefined(),
        requestBody: CapabilitiesDeploymentDescriptorRuntype,
        responseBody: DeployedCapabilitiesRuntype,
        invalidates: ['GET /price/capabilities', 'GET /price/capabilities/:supplier'],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'POST /price/capabilities/:supplier/tables': endpoint({
        description: 'Adds a new capability table to a supplier.',
        pathParams: z.object({ supplier: PcbSupplierReferenceRuntype }),
        queryParams: z.undefined(),
        requestBody: DecisionTableRuntype,
        responseBody: CapabilitiesRuntype,
        invalidates: ['GET /price/capabilities', 'GET /price/capabilities/:supplier'],
        actionPermission: () => 'CanEditPcbPricing',
    }),

    'PUT /price/capabilities/:supplier/tables/:table': endpoint({
        description: 'Deploys a new version of a capability table.',
        pathParams: z.object({ supplier: PcbSupplierReferenceRuntype, table: z.string() }),
        queryParams: z.undefined(),
        requestBody: DecisionTableRuntype,
        responseBody: VersionedDecisionTableRuntype,
        invalidates: ['GET /price/capabilities', 'GET /price/capabilities/:supplier'],
        actionPermission: () => 'CanEditPcbPricing',
    }),
};
